// frontend/super-admin/src/pages/PaymentRequests.jsx
import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Tag,
  Space,
  Modal,
  message,
  Image,
  Descriptions,
  Input,
  InputNumber,
  Form,
  Badge,
  Tabs,
  Empty,
  Alert
} from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  ClockCircleOutlined,
  DollarOutlined
} from '@ant-design/icons';
import api from '../services/api';

const { TextArea } = Input;
const { TabPane } = Tabs;

export default function PaymentRequests() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewModalVisible, setViewModalVisible] = useState(false);
  const [approveModalVisible, setApproveModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [form] = Form.useForm();

  useEffect(() => {
    fetchRequests();
  }, [activeTab]);

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const response = await api.get('/api/payment-requests/all', {
        params: { status: activeTab === 'all' ? undefined : activeTab }
      });
      setRequests(response.data.data || []);
    } catch (error) {
      message.error('Failed to fetch payment requests');
    } finally {
      setLoading(false);
    }
  };

  const handleView = (request) => {
    setSelectedRequest(request);
    setViewModalVisible(true);
  };

  const handleApprove = (request) => {
    setSelectedRequest(request);
    
    // Calculate default duration based on billing cycle
    const defaultDays = request.billingCycle === 'yearly' ? 365 : 30;
    
    form.setFieldsValue({
      durationDays: defaultDays,
      adminNotes: `Payment of ₹${request.amount} verified and approved for ${request.requestedPlanName} plan.`
    });
    
    setApproveModalVisible(true);
  };

  const handleApproveSubmit = async () => {
    try {
      await form.validateFields();
      setProcessing(true);

      const values = form.getFieldsValue();

      await api.post(`/api/payment-requests/${selectedRequest.id}/approve`, values);

      message.success('✅ Payment approved and plan activated!');
      setApproveModalVisible(false);
      fetchRequests();

    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to approve payment');
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = (request) => {
    Modal.confirm({
      title: '❌ Reject Payment Request?',
      content: (
        <div>
          <p>Are you sure you want to reject this payment request?</p>
          <TextArea
            id="rejection-reason"
            placeholder="Enter rejection reason (optional)"
            rows={3}
            style={{ marginTop: 12 }}
          />
        </div>
      ),
      okText: 'Yes, Reject',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        try {
          const rejectedReason = document.getElementById('rejection-reason').value;

          await api.post(`/api/payment-requests/${request.id}/reject`, {
            rejectedReason: rejectedReason || 'Payment verification failed',
            adminNotes: rejectedReason
          });

          message.success('❌ Payment request rejected');
          fetchRequests();

        } catch (error) {
          message.error('Failed to reject payment');
        }
      }
    });
  };

  const columns = [
    {
      title: 'Date',
      dataIndex: 'createdAt',
      key: 'date',
      width: 120,
      render: (date) => new Date(date).toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      })
    },
    {
      title: 'Business',
      key: 'business',
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.tenant?.businessName}</div>
          <div style={{ fontSize: 12, color: '#999' }}>{record.tenant?.email}</div>
        </div>
      )
    },
    {
      title: 'Plan',
      key: 'plan',
      render: (_, record) => (
        <div>
          <Tag color="blue">{record.requestedPlanName}</Tag>
          <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
            From: {record.currentPlan || 'Free'}
          </div>
        </div>
      )
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      render: (amount, record) => (
        <div>
          <div style={{ fontWeight: 600, fontSize: 16 }}>₹{amount}</div>
          <div style={{ fontSize: 11, color: '#999' }}>
            {record.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}
          </div>
        </div>
      )
    },
    {
      title: 'Transaction ID',
      dataIndex: 'upiTransactionId',
      key: 'txnId',
      render: (txnId) => (
        <code style={{ fontSize: 11, background: '#f0f0f0', padding: '2px 6px', borderRadius: 4 }}>
          {txnId || 'N/A'}
        </code>
      )
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const config = {
          pending: { color: 'orange', icon: <ClockCircleOutlined />, text: 'PENDING' },
          approved: { color: 'green', icon: <CheckCircleOutlined />, text: 'APPROVED' },
          rejected: { color: 'red', icon: <CloseCircleOutlined />, text: 'REJECTED' }
        };
        const c = config[status] || config.pending;
        
        return (
          <Tag color={c.color} icon={c.icon}>
            {c.text}
          </Tag>
        );
      }
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
            size="small"
          >
            View
          </Button>

          {record.status === 'pending' && (
            <>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                onClick={() => handleApprove(record)}
                size="small"
                style={{ background: '#52c41a', borderColor: '#52c41a' }}
              >
                Approve
              </Button>

              <Button
                danger
                icon={<CloseCircleOutlined />}
                onClick={() => handleReject(record)}
                size="small"
              >
                Reject
              </Button>
            </>
          )}
        </Space>
      )
    }
  ];

  const pendingCount = requests.filter(r => r.status === 'pending').length;

  return (
    <div>
      <div style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0 }}>💳 Payment Requests</h1>
        <Button icon={<DollarOutlined />} onClick={fetchRequests}>
          Refresh
        </Button>
      </div>

      {pendingCount > 0 && (
        <Alert
          message={`⏰ ${pendingCount} Payment${pendingCount > 1 ? 's' : ''} Pending Approval`}
          type="warning"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />
      )}

      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane 
            tab={
              <Badge count={pendingCount} offset={[10, 0]}>
                <span>Pending</span>
              </Badge>
            } 
            key="pending" 
          />
          <TabPane tab="Approved" key="approved" />
          <TabPane tab="Rejected" key="rejected" />
          <TabPane tab="All" key="all" />
        </Tabs>

        {requests.length === 0 && !loading ? (
          <Empty description="No payment requests" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <Table
            dataSource={requests}
            columns={columns}
            rowKey="id"
            loading={loading}
            pagination={{ pageSize: 10 }}
          />
        )}
      </Card>

      {/* View Modal */}
      <Modal
        title="📄 Payment Request Details"
        open={viewModalVisible}
        onCancel={() => setViewModalVisible(false)}
        footer={null}
        width={700}
      >
        {selectedRequest && (
          <div>
            <Descriptions bordered column={2} size="small">
              <Descriptions.Item label="Business" span={2}>
                <strong>{selectedRequest.tenant?.businessName}</strong>
                <div style={{ fontSize: 12, color: '#999' }}>{selectedRequest.tenant?.email}</div>
              </Descriptions.Item>

              <Descriptions.Item label="Current Plan">
                {selectedRequest.currentPlan || 'Free'}
              </Descriptions.Item>

              <Descriptions.Item label="Requested Plan">
                <Tag color="blue">{selectedRequest.requestedPlanName}</Tag>
              </Descriptions.Item>

              <Descriptions.Item label="Amount">
                ₹{selectedRequest.amount}
              </Descriptions.Item>

              <Descriptions.Item label="Billing Cycle">
                {selectedRequest.billingCycle === 'yearly' ? 'Yearly' : 'Monthly'}
              </Descriptions.Item>

              <Descriptions.Item label="UPI Transaction ID" span={2}>
                <code>{selectedRequest.upiTransactionId || 'N/A'}</code>
              </Descriptions.Item>

              <Descriptions.Item label="Payment Method">
                {selectedRequest.paymentMethod?.toUpperCase() || 'UPI'}
              </Descriptions.Item>

              <Descriptions.Item label="Status">
                <Tag color={
                  selectedRequest.status === 'approved' ? 'green' :
                  selectedRequest.status === 'rejected' ? 'red' :
                  'orange'
                }>
                  {selectedRequest.status?.toUpperCase()}
                </Tag>
              </Descriptions.Item>

              <Descriptions.Item label="Submitted On" span={2}>
                {new Date(selectedRequest.createdAt).toLocaleString('en-IN')}
              </Descriptions.Item>

              {selectedRequest.approvedAt && (
                <Descriptions.Item label="Approved On" span={2}>
                  {new Date(selectedRequest.approvedAt).toLocaleString('en-IN')}
                </Descriptions.Item>
              )}

              {selectedRequest.adminNotes && (
                <Descriptions.Item label="Admin Notes" span={2}>
                  {selectedRequest.adminNotes}
                </Descriptions.Item>
              )}

              {selectedRequest.rejectedReason && (
                <Descriptions.Item label="Rejection Reason" span={2}>
                  <Alert message={selectedRequest.rejectedReason} type="error" showIcon />
                </Descriptions.Item>
              )}
            </Descriptions>

            {/* Screenshot */}
            {selectedRequest.screenshotPath && (
              <div style={{ marginTop: 16 }}>
                <h4>Payment Screenshot:</h4>
                <Image
                  src={`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/${selectedRequest.screenshotPath}`}
                  alt="Payment Screenshot"
                  style={{ 
                    maxWidth: '100%',
                    border: '2px solid #d9d9d9',
                    borderRadius: 8
                  }}
                />
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Approve Modal */}
      <Modal
        title="✅ Approve Payment & Activate Plan"
        open={approveModalVisible}
        onCancel={() => setApproveModalVisible(false)}
        onOk={handleApproveSubmit}
        confirmLoading={processing}
        okText="Approve & Activate"
        okButtonProps={{ style: { background: '#52c41a', borderColor: '#52c41a' } }}
        width={500}
      >
        {selectedRequest && (
          <div>
            <Alert
              message={`Approving payment for ${selectedRequest.tenant?.businessName}`}
              description={`Plan: ${selectedRequest.requestedPlanName} • Amount: ₹${selectedRequest.amount}`}
              type="info"
              showIcon
              style={{ marginBottom: 16 }}
            />

            <Form form={form} layout="vertical">
              <Form.Item
                name="durationDays"
                label="Subscription Duration (Days)"
                rules={[{ required: true, message: 'Please enter duration' }]}
              >
                <InputNumber
                  min={1}
                  max={3650}
                  style={{ width: '100%' }}
                  placeholder="e.g., 30"
                  addonAfter="days"
                />
              </Form.Item>

              <Space style={{ marginBottom: 16 }}>
                <Button size="small" onClick={() => form.setFieldsValue({ durationDays: 30 })}>
                  1 Month
                </Button>
                <Button size="small" onClick={() => form.setFieldsValue({ durationDays: 90 })}>
                  3 Months
                </Button>
                <Button size="small" onClick={() => form.setFieldsValue({ durationDays: 180 })}>
                  6 Months
                </Button>
                <Button size="small" onClick={() => form.setFieldsValue({ durationDays: 365 })}>
                  1 Year
                </Button>
              </Space>

              <Form.Item
                name="adminNotes"
                label="Admin Notes (Optional)"
              >
                <TextArea
                  rows={3}
                  placeholder="e.g., Payment verified via screenshot"
                />
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>
    </div>
  );
}